/**
 * viewport.js — fitting a fixed play area into whatever screen it lands on.
 *
 * The arena is 800x600 and always will be: every spawn, obstacle, beam range
 * and AI distance is tuned against it, and reshaping it is a balance change
 * rather than a layout one. But a phone in landscape is 2.16:1 and the same
 * phone upright is 1:2.16, and neither is 4:3. Something has to give.
 *
 * What gives is the FRAME, not the play area. The canvas is grown to the
 * device's shape; the extra space is filled with more city. So:
 *
 *   - the fighters' world never changes, and nothing needs rebalancing
 *   - no device gets black bars, because the frame is always the screen's shape
 *   - on a wide phone you see further along the rooftop; upright, you see more
 *     sky above and more of the building below
 *
 * The frame is sized against the NARROWEST screen in the game, not against the
 * play area of whichever screen the fight is on. Those used to be the same
 * thing; they stopped being when the outer screens grew (see SCREEN_WIDTHS).
 * Sizing the frame to the widest room instead would mean every device rendering
 * a canvas big enough for a room it is usually not in, to no benefit — the
 * camera already pulls back to fit a bigger room, and pulling back is the
 * cheaper of the two. So the frame's job is only ever to be at least as wide as
 * the smallest room; everything past that is the camera's problem.
 *
 * This is only possible because the backdrop is layered rather than a single
 * photograph — see parallax.js. A photograph has a fixed shape and could only
 * be stretched to fill a frame that did not match it.
 *
 * Everything here is pure arithmetic on a viewport size. The play area's own
 * coordinates are untouched, so the simulation neither knows nor cares.
 */

import { MIN_SCREEN_WIDTH, CANVAS_HEIGHT } from './constants';

/**
 * How far the frame may grow beyond the play area, as a multiple of it.
 *
 * A cap exists because the growth is paid for in pixels: an unbounded frame on
 * a very tall or very wide window would be a huge canvas rendering mostly
 * scenery. The vertical cap has to clear a phone held upright, which is the
 * most extreme shape this ever meets — a 390x844 screen needs 800x1731, or
 * 2.89x. Set below that and portrait quietly letterboxes again, which is the
 * exact problem this module exists to remove.
 */
const MAX_GROW_X = 1.9;
const MAX_GROW_Y = 3.1;

/**
 * Guaranteed clear space each side of the play area, in world px.
 *
 * The containment walls have to sit OUTSIDE everything a fighter's body can
 * reach, and a body is 13px wide either side of a position that clamps 5px from
 * the boundary — so the walls belong 8px beyond the play area, not on it. This
 * reserves somewhere for them to live even in portrait, where the frame is
 * otherwise exactly the width of the play area and there would be nowhere.
 */
export const ARENA_MARGIN = 16;

/**
 * How much of an upright touchscreen's height the thumb console gets.
 *
 * On a phone held upright the arena used to fill the screen and the thumb
 * controls floated on top of it, ending a finger's width from the bottom edge
 * of the device. That is two problems at once: the hand covers the fight, and
 * the fight runs under the hand, so there is nowhere to rest a thumb that is
 * not also somewhere to look. Giving the arena the top of the screen and the
 * thumbs a console below it separates them — you look at one and hold the
 * other.
 *
 * The console is sized to what it holds rather than as a round share: the
 * mobile mock-up gives it 37% of the height, and the floor is the depth its
 * mode switch and three rows of labelled buttons need at a thumb-sized 48px
 * — which on a short phone is more than 37%, so the arena gives way there.
 * The arena keeps the rest, which is taller than the old half: the header
 * rides over its sky, and the fight keeps the same room underneath it.
 *
 * Landscape is untouched. There the thumbs are already beside the fight rather
 * than under it, which is why the game asks to be held that way.
 */
export const PORTRAIT_CONSOLE_SHARE = 0.37;
export const PORTRAIT_CONSOLE_MIN = 256;

/**
 * The box the arena is drawn into, in CSS pixels.
 *
 * The single source for BOTH the canvas element's size and the frame computed
 * for it below — those two have to agree exactly or the pixel art is stretched
 * by whatever they disagree by, and having CSS own one while JavaScript owned
 * the other is precisely how they would come to disagree.
 *
 * @param {number} vw Viewport width in CSS px.
 * @param {number} vh Viewport height in CSS px.
 * @param {boolean} [thumbRoom] Reserve the lower part of the screen for the
 *   on-screen controls — an upright touchscreen, and nothing else.
 * @returns {{width: number, height: number}}
 */
export function stageBox(vw, vh, thumbRoom = false) {
  return {
    width: vw,
    height: thumbRoom
      ? Math.round(vh - Math.max(vh * PORTRAIT_CONSOLE_SHARE, PORTRAIT_CONSOLE_MIN))
      : vh,
  };
}

/** @returns {boolean} whether two stage boxes differ. */
export function stageChanged(a, b) {
  return !a || a.width !== b.width || a.height !== b.height;
}

/**
 * Work out the frame for a given viewport.
 *
 * The result has the box's aspect exactly — the canvas is stretched to this
 * frame, so any disagreement between the two is a distortion of the pixel art —
 * and is grown until it contains the narrowest screen whole, so the landing
 * stage is never cropped by the frame. The wider screens ARE larger than the
 * frame, by design: that is what the camera is for, and it guarantees both
 * fighters are on screen whatever the room's size.
 *
 * @param {number} vw Viewport width in CSS px.
 * @param {number} vh Viewport height in CSS px.
 * @returns {{width: number, height: number, offsetX: number, offsetY: number}}
 *   Frame size in world units, and where the play area's origin sits inside it.
 */
export function computeView(vw, vh) {
  const aspect = vw > 0 && vh > 0 ? vw / vh : MIN_SCREEN_WIDTH / CANVAS_HEIGHT;

  // The frame is the BOX'S OWN SHAPE, grown until it contains the narrowest
  // screen and the full play height. Deriving the height from the width like
  // this — rather than growing each axis independently against its own
  // minimum — is what keeps the frame's aspect exactly the box's.
  //
  // Independently was the old way and it quietly stretched the art. On a phone
  // upright the width was floored at 832 while the natural width for that shape
  // was about 550, so the frame came out 4% wider in proportion than the box it
  // was displayed in, and every sprite was drawn 4% fat. Invisible enough to
  // survive a long time and wrong all the same.
  const minWidth = MIN_SCREEN_WIDTH + ARENA_MARGIN * 2;
  let width = Math.max(minWidth, CANVAS_HEIGHT * aspect);
  let height = width / aspect;

  // A ceiling on how large the pixel buffer can get, applied to both axes by
  // the same factor so the shape survives it. Only reachable at shapes no
  // handheld device has — past roughly 32:9, or a window three times taller
  // than it is wide.
  const shrink = Math.min(
    1,
    (MIN_SCREEN_WIDTH * MAX_GROW_X) / width,
    (CANVAS_HEIGHT * MAX_GROW_Y) / height
  );
  width = Math.round(width * shrink);
  height = Math.round(height * shrink);

  return {
    width,
    height,
    offsetX: (width - MIN_SCREEN_WIDTH) / 2,
    // Biased downward, so most of the extra height goes above the roofline:
    // everything worth looking at is up there — sky, skyline, the fight — while
    // below it is the flat top of a building.
    //
    // Not biased further, though, and the limit is ergonomic rather than
    // aesthetic. On a phone held upright the thumbs need the band beneath the
    // fight; push the play area much lower and the controls start clipping its
    // bottom edge, which is worse than a little extra deck. Only has any effect
    // when the frame is taller than the play area, i.e. in portrait.
    offsetY: Math.round((height - CANVAS_HEIGHT) * 0.45),
  };
}

/** @returns {boolean} whether two views differ enough to warrant a re-render. */
export function viewChanged(a, b) {
  return !a || a.width !== b.width || a.height !== b.height
    || a.offsetX !== b.offsetX || a.offsetY !== b.offsetY;
}
